import { inject, Inject, Injectable } from '@angular/core';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { App } from '@df/core/app';
import { Prefs } from '@df/session/prefs';
import { BehaviorSubject } from 'rxjs';
import { NUi } from '../df-ui.config';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { Nexus } from '@tomandco/nexus-api-types';

export interface IPriceJson {
    // integer part (number)
    value: number;
    // decimal part (string)
    cent: string;
    // price precision
    precision: number;
    // currency symbol
    symbol: string;
}

/**
 * Tax calculation modes
 */
export enum TaxMode {
    /**
     * Ignore tax, pass result directly
     */
    skip,
    /**
     * Follow user selection or website defaults
     */
    auto,
    /**
     * Including tax
     */
    including,
    /**
     * Excluding tax
     */
    excluding
}

/**
 * Tax settings area
 */
export enum TaxArea {
    /**
     * catalog area (products)
     */
    catalog,
    /**
     * basket area (items)
     */
    basket,
    /**
     * order area (order)
     */
    order,
    /**
     * Shipping
     */
    shipping
}

@Injectable({
    providedIn: 'root'
})
export class PriceService {
    private prefs = inject(Prefs);
    protected app = inject(App);
    private localStorageService = inject(LocalStorageService);
    private dfApiConfigService = inject(DfApiConfigService);
    private uiConfig = inject(NUi.OPTIONS);
    readonly catalogPricesInclTax$ = new BehaviorSubject(this.getInitCatalogPricesInclTaxValue());

    /**
     * Price config shorthand
     */
    get config() {
        return this.uiConfig.price;
    }

    /**
     * Free label text
     */
    get freeLabel(): string {
        return this.config.freeLabelText;
    }

    constructor() {
        this.init();
    }

    private async init() {
        await this.dfApiConfigService.loadApiConfig();

        const initialCatalogPricesInclTaxValue = this.getInitCatalogPricesInclTaxValue();
        if (this.catalogPricesInclTax$.value !== initialCatalogPricesInclTaxValue) {
            this.catalogPricesInclTax$.next(initialCatalogPricesInclTaxValue);
        }
    }

    private getInitCatalogPricesInclTaxValue(): boolean {
        const localStorageValue = this.localStorageService.get('catalogPricesInclTax');
        if (localStorageValue !== undefined) {
            return !!localStorageValue;
        }
        const moduleConfigValue = this.uiConfig.price.initDisplayTaxWith;
        if (moduleConfigValue !== undefined) {
            return !!moduleConfigValue;
        }
        // if not yet loaded (may happen before init is complete)
        if (!this.app.data) {
            return true;
        }
        return this._shouldTaxBeIncludedInCatalogPrices();
    }

    /**
     * Returns json data about price value and its precisions
     * @param  {number} value Price value
     * @param  {number | string} precision Optional price precision
     * @return {object}
     */
    toJson(value: number, precision: number | string = this.config.precision): IPriceJson | undefined {
        if (typeof value !== 'number') {
            return undefined;
        }

        // take current currency
        const currency = this.prefs.getCurrency();

        if (!currency) {
            return undefined;
        }

        // calculate value in that currency
        let valueInCurrency: number = currency ? value * currency.rate : value;

        const sign: number = valueInCurrency < 0 ? -1 : 1;
        valueInCurrency = Math.abs(valueInCurrency);

        // set auto precision if requested
        if (precision === 'auto') {
            precision = valueInCurrency % 1 > 0 ? 2 : 0;
        }

        valueInCurrency = this.round(valueInCurrency);

        // take integer part
        const intValue: number = Math.floor(valueInCurrency);

        // set cents
        const centValue: string = (valueInCurrency - intValue).toFixed(<number>precision).substr(2);

        return {
            // integer part (number)
            value: sign * intValue,
            // decimal part (string)
            cent: centValue,
            // price precision
            precision: <number>precision,
            // currency symbol
            symbol: <string>currency.symbol
        };
    }

    /**
     * if thousand separator is requested then use it
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    private applyThousendSeparator(value: number | string): number | string {
        if (this.config.thousandSeparator) {
            return ('' + value).replace(/\B(?=(\d{3})+(?!\d))/g, this.config.thousandSeparator);
        }
        return value;
    }

    /**
     * Returns price string with html classes on each element
     */
    toHtmlString(value: number, precision: number | string = this.config.precision, currencySymbol?: string): string | undefined {
        return this.toString(value, precision, true, currencySymbol);
    }

    /**
     * Returns price string with html classes on each element
     */
    toPlainString(value: number, precision: number | string = this.config.precision, currencySymbol?: string): string | undefined {
        return this.toString(value, precision, false, currencySymbol);
    }

    /**
     * Returns price string with html classes on each element
     */
    private toString(
        value: number,
        precision: number | string = this.config.precision,
        useHtml: boolean = true,
        currencySymbol?: string
    ): string | undefined {
        const json = this.toJson(value, precision);
        if (!json) {
            return undefined;
        }

        if (!currencySymbol) {
            currencySymbol = json.symbol;
        }

        const symbolString = this.wrapHtml('price-symbol', useHtml, currencySymbol);

        let priceString = this.wrapHtml('price-value', useHtml, this.applyThousendSeparator(Math.abs(json.value)));

        if (json.precision) {
            priceString += ((this.wrapHtml('price-separator', useHtml, this.config.decimalSeparator) as string) +
                this.wrapHtml('price-cent', useHtml, json.cent)) as string;
        }

        if (this.config.symbolAfterPrice === true) {
            priceString = priceString + this.config.symbolSeparator + symbolString;
        } else {
            priceString = symbolString + this.config.symbolSeparator + priceString;
        }

        if (value < 0) {
            priceString = '-' + priceString;
        }

        return priceString;
    }

    private wrapHtml(className: string, useHtml: boolean, content: string | number) {
        return useHtml ? `<span class="price-symbol">${content}</span>` : content;
    }

    /**
     * If catalog prices should be presented with tax
     */
    get showCatalogPricesInclTax(): boolean {
        return this.catalogPricesInclTax$.value;
    }

    /**
     * Sets diaplay mode for catalog prices
     */
    public set showCatalogPricesInclTax(to: boolean) {
        if (to !== this.catalogPricesInclTax$.value) {
            this.catalogPricesInclTax$.next(to);
            this.localStorageService.set('catalogPricesInclTax', to);
        }
    }

    /**
     * Sets tax for price
     * To be used for catalog prices only as we check if that price contains tax or not
     * @param price - price value
     * @param tax  - IF POSITIVE tax rate in % (so 20 means 20% tax), IF NEGATIVE tax value
     * @param area  - tax area or boolean if you want to force original price having tax or not
     */
    applyTax(price: number, tax?: number, taxMode: TaxMode = TaxMode.skip, taxArea: TaxArea | boolean = TaxArea.catalog): number {
        if (tax && taxMode !== TaxMode.skip) {
            const areaPricesExclTax = typeof taxArea === 'boolean' ? !taxArea : !this.hasTax(taxArea);
            let showAreaPricesExclTax: boolean;
            switch (taxMode) {
                case TaxMode.including:
                    showAreaPricesExclTax = false;
                    break;
                case TaxMode.excluding:
                    showAreaPricesExclTax = true;
                    break;
                default:
                    showAreaPricesExclTax = !this.shouldTaxBeIncluded(typeof taxArea === 'boolean' ? TaxArea.catalog : taxArea);
            }
            if (areaPricesExclTax !== showAreaPricesExclTax) {
                const roundTaxMode = areaPricesExclTax ? TaxMode.including : TaxMode.excluding;
                // if tax value
                if (tax < 0) {
                    // WARNING: sign is reversed cause we have negative tax value for tech reasons
                    return this.round(areaPricesExclTax ? price - tax : price + tax, roundTaxMode);
                }
                // if tax rate
                return this.round(areaPricesExclTax ? price * (tax / 100 + 1) : (price * 100) / (100 + tax), roundTaxMode);
            }
        }
        return this.round(price);
    }

    /**
     * Checks if prices of given area contain tax when coming from BE
     */
    hasTax(taxArea: TaxArea): boolean {
        switch (taxArea) {
            case TaxArea.basket:
                return this.isTaxIncludedInBasketPrices();
            case TaxArea.shipping:
                return this.isTaxIncludedInShippingPrices();
            case TaxArea.catalog:
                return this.isTaxIncludedInCatalogPrices();
            case TaxArea.order:
                return this.isTaxIncludedInOrderPrices();
        }
        return true;
    }
    /**
     * Checks if prices of given area contain tax when coming from BE
     */
    shouldTaxBeIncluded(taxArea: TaxArea): boolean {
        switch (taxArea) {
            case TaxArea.basket:
                return this.shouldTaxBeIncludedInBasketPrices();
            case TaxArea.shipping:
                return this.shouldTaxBeIncludedInShippingPrices();
            case TaxArea.catalog:
                return this.shouldTaxBeIncludedInCatalogPrices();
        }
        return true;
    }

    round(price: number, taxMode?: TaxMode): number {
        return Math.round(100 * price) / 100;
    }

    /**
     * If basket area prices (basket, basket items) comming from BE include tax
     */
    isTaxIncludedInBasketPrices(): boolean {
        const settings = this.app.data.tax.basket;
        if ('includeTax' in settings) {
            return settings.includeTax;
        }
        return !settings.exclude;
    }

    /**
     * If shipping total value from BE includes shipping tax
     */
    isTaxIncludedInShippingPrices(): boolean {
        const settings = this.app.data.tax.shipping;
        if ('includeTax' in settings) {
            return settings.includeTax;
        }
        return !settings.exclude;
    }

    /**
     * If catalog prices should be shown including tax
     */
    isTaxIncludedInCatalogPrices(): boolean {
        const settings = this.app.data.tax.catalog;
        if ('includeTax' in settings) {
            return settings.includeTax;
        }
        return !settings.exclude;
    }
    /**
     * If catalog prices should be shown including tax
     */
    isTaxIncludedInOrderPrices(): boolean {
        if ('order' in this.app.data.tax) {
            return (this.app.data.tax as Nexus.Lib.General.TaxSettings).order.includeTax;
        }
        return this.isTaxIncludedInCatalogPrices();
    }

    /**
     * If baskset prices should be shown including tax
     */
    shouldTaxBeIncludedInBasketPrices(): boolean {
        const settings = this.app.data.tax.basket;
        if ('displayIncludingTax' in settings) {
            return settings.displayIncludingTax;
        }
        return settings.displayTax;
    }

    /**
     * If baskset prices should be shown including tax
     */
    shouldTaxBeIncludedInOrderPrices(): boolean {
        if ('order' in this.app.data.tax) {
            return (this.app.data.tax as Nexus.Lib.General.TaxSettings).order.displayIncludingTax;
        }
        return this.shouldTaxBeIncludedInBasketPrices();
    }

    /**
     * If shipping prices should be shown including tax
     */
    shouldTaxBeIncludedInShippingPrices(): boolean {
        const settings = this.app.data.tax.shipping;
        if ('displayIncludingTax' in settings) {
            return settings.displayIncludingTax;
        }
        return settings.displayTax;
    }

    /**
     * If catalog prices should be shown including tax
     */
    shouldTaxBeIncludedInCatalogPrices(): boolean {
        return this.showCatalogPricesInclTax;
    }

    /**
     * Config value, but we allowe override by storage selection or module config
     */
    private _shouldTaxBeIncludedInCatalogPrices(): boolean {
        const settings = this.app.data.tax.catalog;
        if ('displayIncludingTax' in settings) {
            return settings.displayIncludingTax;
        }
        return settings.displayTax;
    }

    /**
     * If tax breakdown should be shown for basket
     */
    shouldTaxBreakdownBeShownForBasket(): boolean {
        const settings = this.app.data.tax.basket;
        if ('showTaxBreakdown' in settings) {
            return (this.app.data.tax as Nexus.Lib.General.TaxSettings).basket.showTaxBreakdown;
        }
        if (!this.isTaxIncludedInBasketPrices() || !this.isTaxIncludedInShippingPrices()) {
            return true;
        }
        return false;
    }

    getSubtotal(
        totals: Nexus.Lib.Basket.Totals | undefined,
        sourceDataTaxArea: TaxArea = TaxArea.basket,
        taxMode: TaxMode = TaxMode.skip
    ): number {
        return this.getTotal(totals, sourceDataTaxArea, 'subtotal', 'subtotal_tax', taxMode);
    }

    getDiscount(
        totals: Nexus.Lib.Basket.Totals | undefined,
        sourceDataTaxArea: TaxArea = TaxArea.basket,
        desiredTaxMode: TaxMode = TaxMode.skip
    ): number {
        return this.getTotal(totals, sourceDataTaxArea, 'discount', 'discount_tax', desiredTaxMode);
    }

    getShipping(
        totals: Nexus.Lib.Basket.Totals | undefined,
        sourceDataTaxArea: TaxArea = TaxArea.shipping,
        taxMode: TaxMode = TaxMode.skip
    ): number {
        return this.getTotal(totals, sourceDataTaxArea, 'shipping', 'shipping_tax', taxMode);
    }

    getTotal(
        totals: Nexus.Lib.Basket.Totals | undefined,
        sourceDataTaxArea: TaxArea,
        totalId: string,
        taxTotalId: string,
        desiredTaxMode: TaxMode = TaxMode.skip
    ): number {
        if (totals && totalId in totals) {
            const value = totals[totalId];
            return (
                this.applyTax(Math.abs(value), -Math.abs(totals[taxTotalId] || 0), desiredTaxMode, sourceDataTaxArea) * (value < 0 ? -1 : 1)
            );
        }
        return 0;
    }
}
